import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useBoolean, useSetState, useUpdateEffect } from 'ahooks';
import dayjs from 'dayjs';
import { debounce } from 'lodash-es';
import { Icon } from '@fuxi/eevee-icon';
import { Skeleton } from 'antd';
import emptyBg from '@/assets/image/order-empty.png';
import { ScrollView, SlideModal } from '@/components';
import service from '@/service';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { DAYJS_FORMAT } from '@/constant';
import { getLastSevenDays } from '@/pages/service-record/components/WeekTimeSelect';
import { showTimeText } from '../../help-feedback';
import SlideFilter from './slide-filter';
import cx from './index.module.scss';

interface Props {
  onSelectOrder: (orderInfo: GetFinishedOrderListResponse) => void;
}

export interface SelectOrderRefAttrs {
  show: (orderInfo: GetFinishedOrderListResponse) => void;
  hide: VoidFunc;
}

export const STATES_FILTER = [
  { value: 'Serving', text: '服务中订单', stateText: '服务中' },
  { value: 'Completed', text: '自动结束订单', stateText: '自动结束' },
  { value: 'Abnormal', text: '异常结束订单', stateText: '异常结束' },
  { value: 'Terminated', text: '人工结束订单', stateText: '人工结束' },
];

const SelectOrder = forwardRef<SelectOrderRefAttrs, Props>(({ onSelectOrder }, ref) => {
  const [isDrawerShow, { setTrue: setDrawerShow, setFalse: setDrawerHide }] = useBoolean(false);
  const [selectOrderInfo, setSelectOrderInfo] = useState<GetFinishedOrderListResponse>({} as any);
  const [deviceFilterList, setDeviceFilterList] = useState([]);
  const [filterInfo, setFilterInfo] = useSetState<GetFinishedOrderListParams>({
    date: dayjs().format(DAYJS_FORMAT.DATE),
    states: [],
    deviceIds: [],
  });
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const [orderList, setOrderList] = useState<GetFinishedOrderListResponse[]>([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);

  const isDisabledSubmit = useMemo(() => !selectOrderInfo?.id, [selectOrderInfo?.id]);

  const initData = useCallback(async () => {
    const { data } = await service.device.getStoreDeviceList();

    setDeviceFilterList(
      data.map(item => ({
        value: item.id,
        text: item.name,
        subText: item?.id,
      }))
    );
  }, []);

  const getOrderList = useMemo(
    () =>
      debounce(async (params: GetFinishedOrderListParams) => {
        setIsLoadingOrder(true);
        try {
          const states = [...params?.states];
          // 如果用户选中了服务中的订单 需要传两个参数 Serving and Created
          const index = states?.indexOf('Serving');
          if (index !== -1) {
            states?.splice(index + 1, 0, 'Created');
          }
          const { data } = await service.service.getFinishedOrderList({ ...params, states });

          setOrderList(data);
        } finally {
          setIsLoadingOrder(false);
        }
      }, 300),
    []
  );

  useImperativeHandle(
    ref,
    () => ({
      show: orderInfo => {
        setSelectOrderInfo(orderInfo || ({} as any));
        setDrawerShow();
      },
      hide: setDrawerHide,
    }),
    [setDrawerHide, setDrawerShow]
  );

  useUpdateEffect(() => {
    if (!isDrawerShow) {
      setFilterInfo({
        date: dayjs().format(DAYJS_FORMAT.DATE),
        states: [],
        deviceIds: [],
      });
    }
  }, [isDrawerShow]);

  useEffect(() => {
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    isDrawerShow && getOrderList(filterInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterInfo, isDrawerShow]);

  useEffect(() => {
    if (orderList?.length) {
      scrollViewRef.current?.handleRefresh();
    }
  }, [orderList?.length]);

  return (
    <SlideModal open={isDrawerShow} className={cx('slide-modal')}>
      <div className={cx('select-box')}>
        <div className={cx('title')}>
          <div className={cx('title-text')}>订单选择</div>
          <div className={cx('btns')}>
            <div onClick={setDrawerHide}>取消</div>
            <div
              className={cx('confirm', { disabled: isDisabledSubmit })}
              onClick={() => {
                if (isDisabledSubmit) return;
                onSelectOrder?.(selectOrderInfo);
                setDrawerHide();
              }}>
              确认
            </div>
          </div>
        </div>
        <div className={cx('filter-box')}>
          <SlideFilter
            className={cx('filter-item')}
            filterName="日期"
            value={[filterInfo.date]}
            onChange={value => setFilterInfo({ date: value[0] })}
            list={getLastSevenDays().map(item => ({
              value: item.date,
              text: item.showText,
            }))}
            multiple={false}
          />
          <SlideFilter
            className={cx('filter-item')}
            filterName="设备"
            value={filterInfo.deviceIds}
            onChange={value => setFilterInfo({ deviceIds: value })}
            list={deviceFilterList}
            allSelectText="全部设备"
          />
          <SlideFilter
            className={cx('filter-item')}
            filterName="结束类型"
            value={filterInfo.states}
            onChange={value => setFilterInfo({ states: value as any })}
            list={STATES_FILTER}
            allSelectText="全部订单"
          />
        </div>
        <div className={cx('scroll-box')}>
          <ScrollView ref={scrollViewRef} className={cx('order-list-wrapper')}>
            <Skeleton loading={isLoadingOrder} title={false} paragraph={{ rows: 6, width: '100%' }}>
              {!!orderList?.length ? (
                <div className={cx('order-list-box')}>
                  {orderList.map(item => (
                    <div key={item.id} className={cx('order-item')} onClick={() => setSelectOrderInfo(item)}>
                      {selectOrderInfo?.id === item.id && (
                        <div className={cx('selected')}>
                          <div className={cx('infos')}>
                            <div className={cx('select-text')}>已选择</div>
                            <Icon size={24} name="success" color="#0055FF" />
                          </div>
                        </div>
                      )}
                      <div className={cx('header')}>
                        <div className={cx('order-name')}>{item.goodsName}</div>
                        <div className={cx('order-state', item.state)}>
                          {STATES_FILTER.find(item2 => item2.value === item.state)?.stateText}
                        </div>
                      </div>
                      <div className={cx('content')}>
                        <div className={cx('info-item')}>设备：{item.deviceId}</div>
                        <div className={cx('info-item')}>下单时间：{showTimeText(item.createTime)}</div>
                        {item.state === 'Abnormal' && (
                          <div className={cx('info-item')}>
                            <span className={cx('abnormal-time')}>异常时间：{showTimeText(item.abnormalTime)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={cx('empty-box')}>
                  <img src={emptyBg} alt="" />
                  <div className={cx('empty-text')}>暂无订单</div>
                </div>
              )}
            </Skeleton>
          </ScrollView>
        </div>
      </div>
    </SlideModal>
  );
});

export default SelectOrder;
